import { Select } from 'antd'
import React, { FC, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { KVAnnotation } from 'types'

import {
  getNavyCategoryForDamageType,
  navyDamageCategories,
} from './navyDamageTypes'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`

const NavyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const CategoryButton = styled.button<{ $selected: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ $selected }) => ($selected ? '#4CAF50' : '#666666')};
  background-color: ${({ $selected }) => ($selected ? '#2E7D32' : '#000000')};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 0.75rem;
  flex: 1;

  &:hover {
    background-color: ${({ $selected }) => ($selected ? '#2E7D32' : '#444444')};
    border-color: ${({ $selected }) => ($selected ? '#4CAF50' : '#888888')};
  }
`

const CategoryButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  align-items: stretch;
  flex-shrink: 0;
`

const DamageButtonGrid = styled.div<{ $isExiting: boolean }>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  animation: ${({ $isExiting }) => ($isExiting ? fadeOut : fadeIn)} 0.2s
    ease-out;
  animation-fill-mode: forwards;
  padding-top: 0.5rem;
  justify-content: center;
  width: 100%;
`

const DamageButton = styled.button<{ $selected: boolean }>`
  width: 100px;
  height: 70px;
  border: 1px solid ${({ $selected }) => ($selected ? '#4CAF50' : '#666666')};
  background-color: ${({ $selected }) => ($selected ? '#2E7D32' : '#000000')};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.1s ease;
  padding: 0.2rem;

  &:hover {
    background-color: ${({ $selected }) => ($selected ? '#2E7D32' : '#444444')};
    border-color: ${({ $selected }) => ($selected ? '#4CAF50' : '#888888')};
  }
`

const ButtonText = styled.div`
  font-size: 0.55rem;
  text-align: center;
  line-height: 1.1;
  color: rgba(255, 255, 255, 0.8);
  word-wrap: break-word;
  max-width: 100%;
`

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background-color: ${({ theme }) => theme.forecast.background3} !important;
    border-color: ${({ theme }) => theme.forecast.border} !important;
    color: ${({ theme }) => theme.forecast.activeText} !important;
    font-size: 0.75rem !important;
  }

  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText} !important;
  }

  &:hover .ant-select-selector {
    border-color: ${({ theme }) => theme.forecast.border} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.forecast.primary} !important;
    box-shadow: none !important;
  }
`

const navySectionOptions = [
  { value: 'top', label: 'Top' },
  { value: 'sides', label: 'Sides/Bulkhead/Shell' },
  { value: 'stiffeners', label: 'Stiffeners/T-Bars' },
  { value: 'bottom', label: 'Bottom/Deck' },
]

interface NavyDamageSelectorProps {
  selectedTypes: string[]
  onSelectionChange: (types: string[]) => void
  annotation: KVAnnotation
  setAnnotation: (annotation: KVAnnotation) => void
}

export const NavyDamageSelector: FC<NavyDamageSelectorProps> = ({
  selectedTypes,
  onSelectionChange,
  annotation,
  setAnnotation,
}) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [isExiting, setIsExiting] = useState(false)
  const [displayCategory, setDisplayCategory] = useState<string | null>(null)

  useEffect(() => {
    if (selectedTypes.length > 0) {
      const selectedType = selectedTypes[0]
      const categoryToOpen = getNavyCategoryForDamageType(selectedType)

      if (categoryToOpen) {
        setActiveCategory(categoryToOpen)
        setDisplayCategory(categoryToOpen)
      }
    }
  }, [selectedTypes])

  const toggleDamageType = (damageTypeId: string) => {
    if (selectedTypes.includes(damageTypeId)) {
      onSelectionChange([])
    } else {
      onSelectionChange([damageTypeId])
    }
  }

  const handleCategoryClick = (category: string) => {
    if (activeCategory === category) {
      setIsExiting(true)
      setTimeout(() => {
        setActiveCategory(null)
        setDisplayCategory(null)
        setIsExiting(false)
      }, 200)
    } else {
      if (activeCategory) {
        setIsExiting(true)
        setTimeout(() => {
          setActiveCategory(category)
          setDisplayCategory(category)
          setIsExiting(false)
        }, 200)
      } else {
        setActiveCategory(category)
        setDisplayCategory(category)
      }
    }
  }

  const disabled = annotation.historical

  const handleSectionChange = (value: unknown) => {
    if (typeof value !== 'string' && value !== null && value !== undefined)
      return

    if (!annotation.navyAnnotation) return

    setAnnotation({
      ...annotation,
      navyAnnotation: {
        ...annotation.navyAnnotation,
        section: value || '',
      },
    })
  }

  return (
    <NavyContainer>
      <StyledSelect
        placeholder='Select Section'
        value={annotation.navyAnnotation?.section || null}
        onChange={handleSectionChange}
        disabled={disabled}
        allowClear
        options={navySectionOptions}
      />

      <CategoryButtonsContainer>
        {navyDamageCategories.map((category) => (
          <CategoryButton
            key={category.id}
            disabled={annotation.historical}
            $selected={activeCategory === category.id}
            onClick={() => handleCategoryClick(category.id)}>
            {category.label}
          </CategoryButton>
        ))}
      </CategoryButtonsContainer>

      {displayCategory && (
        <DamageButtonGrid $isExiting={isExiting}>
          {navyDamageCategories
            .find((cat) => cat.id === displayCategory)
            ?.types.map((type) => (
              <DamageButton
                key={type.id}
                disabled={disabled}
                $selected={selectedTypes.includes(type.id)}
                onClick={() => toggleDamageType(type.id)}>
                <ButtonText>{type.label}</ButtonText>
              </DamageButton>
            ))}
        </DamageButtonGrid>
      )}
    </NavyContainer>
  )
}
